/**
 * FRONT-1578 — pure decision logic for the deep-research detail panel's
 * open/closed state, extracted out of inbox-chat-modal.tsx (same reasoning as
 * research-banner-logic.ts: unit-testable without React) so the two rules
 * below can be pinned exhaustively.
 *
 * The product ask (Mattias): the panel now opens itself the moment a run
 * begins — no more hunting for the bar to click — but it must never fight the
 * user. Once they fold it back down, it stays down for THAT run; only a
 * genuinely different run (a new run id) earns a fresh auto-open. And sending
 * a message mid-run folds the panel away on its own, so a reply the user is
 * about to read never gets buried under the research detail.
 *
 * ## Why "already auto-opened" lives outside the open/closed boolean
 *
 * A plain "open" boolean can't tell "the user closed this" apart from
 * "nothing has opened it yet" — both read as `false`. Recomputing "should
 * this be open" on every render off `open` alone would either never
 * auto-open (if it deferred to a false `open`) or re-open itself right after
 * every manual collapse (if it didn't). `autoOpenedRunId` is the memory that
 * breaks the tie: once it equals the run's id, {@link nextResearchPanelState}
 * treats that run as "already handled" and leaves `open` exactly as the
 * caller passed it in, however it got there.
 *
 * The caller (`inbox-chat-modal.tsx`) is expected to keep `autoOpenedRunId`
 * in a `useRef`, not a `useState` — it only ever needs to be read the next
 * time this function runs, never to trigger a render itself.
 */

/** A run id ({@link useResearch}'s `runningResearchId`) or `null` when no
 *  confirmed run is currently showing. */
export type ResearchRunId = string | null;

export interface ResearchPanelState {
  /** Whether the detail panel is currently open over the conversation. */
  open: boolean;
  /**
   * The run id the panel has already auto-opened itself for, or `null` if it
   * hasn't auto-opened for any run yet. Once set, a later `run-active` tick
   * for the SAME id is a no-op — that's what stops a manual collapse from
   * being undone on the next re-render.
   */
  autoOpenedRunId: ResearchRunId;
}

export type ResearchPanelEvent =
  /** A run is (still, or newly) the one the bar is showing for. Fired
   *  whenever the confirmed run id is known and may have changed — including
   *  every render where it hasn't, which is why this must be a no-op unless
   *  the id is genuinely new. */
  | 'run-active'
  /** The user actually SENT a message (composer submit) — never focus, never
   *  a keystroke. */
  | 'message-sent';

/**
 * Computes the panel's next `{ open, autoOpenedRunId }` from its current
 * state, the run id currently active (or `null`), and what happened.
 *
 * - `run-active` with no confirmed run id yet (still in the brief optimistic
 *   window before the real run row arrives) is a no-op — there is no id yet
 *   to remember, so nothing can be auto-opened for it.
 * - `run-active` with a run id already recorded in `autoOpenedRunId` is a
 *   no-op — this run has already had its one auto-open, whatever the user
 *   did with it since.
 * - `run-active` with a genuinely new run id opens the panel and records
 *   that id — this is the one and only auto-open a given run gets.
 * - `message-sent` folds the panel away if it's open, and does nothing
 *   otherwise. It never needs the run id: the panel can only be open while
 *   a run is showing (the caller force-collapses it the instant the run's
 *   bar disappears — see the effect in inbox-chat-modal.tsx), so "is it
 *   open" already implies "is a run active."
 *
 * Returns the SAME state reference when nothing changes, so a caller that
 * feeds this straight into `useState`/a ref comparison doesn't re-render or
 * re-write on a no-op tick — same convention as `fetchResearchTimelinePage`
 * in use-research.ts.
 */
export function nextResearchPanelState(
  state: ResearchPanelState,
  runId: ResearchRunId,
  event: ResearchPanelEvent,
): ResearchPanelState {
  if (event === 'message-sent') {
    if (!state.open) return state;
    return { open: false, autoOpenedRunId: state.autoOpenedRunId };
  }

  // event === 'run-active'
  if (runId == null) return state;
  if (state.autoOpenedRunId === runId) return state;
  return { open: true, autoOpenedRunId: runId };
}
